import React from 'react';
import { useQuery } from '@apollo/client';
import { Box, Typography, CircularProgress } from '@material-ui/core';

import { GET_PLAYER_UPCOMING_ATTENDANCE } from '../../../lib/queries/attendance-response.queries';
import { isUnauthorizedTokenError } from './public-edit-player.helpers';
import { SessionRow } from './session-row.component';
import { PlayerPageCard } from './player-page-card.component';

const mapTableError = (error?: { message?: string } | null): string => {
  if (isUnauthorizedTokenError(error)) {
    return 'This link has expired or is no longer valid. Please open the most recent link from your email or text, or contact your organization for a new one.';
  }
  return "We couldn't load the upcoming ice times. Please try again in a moment, or contact your organization for help.";
};

interface IPublicAttendanceTableProps {
  playerAssessmentId: string;
  token: string | null;
  onSelectSession: (assessmentSessionId: string) => void;
}

// Token-authenticated attendance dashboard — the default view of the public
// edit-player page. Lists every upcoming ice time for the player; tapping a
// row's RSVP button opens the focused per-session view (the page-shell handles
// the swap). `cache-and-network` ensures fresh aggregates on every re-mount —
// the focused view's submit invalidates session+response state the cache can't
// recompute, and this table is re-mounted on every back navigation.
export const PublicAttendanceTable: React.FC<IPublicAttendanceTableProps> = ({
  playerAssessmentId,
  token,
  onSelectSession,
}) => {
  const { data, loading, error } = useQuery(GET_PLAYER_UPCOMING_ATTENDANCE, {
    variables: { playerAssessmentId, token },
    fetchPolicy: 'cache-and-network',
  });

  const sessions: IPlayerAttendanceSession[] = (data && data.playerUpcomingAttendance) || [];

  let body: React.ReactNode;
  if (loading && !sessions.length) {
    body = (
      <Box textAlign="center" py={3}>
        <CircularProgress size={28} />
      </Box>
    );
  } else if (error) {
    body = (
      <Typography variant="body2" color="textSecondary">
        {mapTableError(error)}
      </Typography>
    );
  } else if (!sessions.length) {
    body = (
      <Typography variant="body2" color="textSecondary">
        No upcoming ice times right now. Check back once your next one is scheduled.
      </Typography>
    );
  } else {
    // Last card's bottom margin is absorbed so the outer card's padding stays
    // visually balanced — each card otherwise contributes its own gap.
    body = (
      <Box mt={1} style={{ marginBottom: -12 }}>
        {sessions.map((session) => (
          <SessionRow
            key={session.assessmentSessionId}
            session={session}
            onSelect={onSelectSession}
          />
        ))}
      </Box>
    );
  }

  return (
    <PlayerPageCard gutterTop gutterBottom>
      <Typography variant="h6" gutterBottom>Upcoming ice times</Typography>
      {body}
    </PlayerPageCard>
  );
};
